import { useEffect, useState, useCallback } from "react";
import { X, LibraryBig, FileText, Image as ImageIcon, Trash2, Download, File as FileIcon } from "lucide-react";
import { cn } from "@/lib/utils";
import { Tip } from "@/components/Tip";
import {
  fetchFiles,
  deleteFile,
  fileDownloadUrl,
  UserFileItem,
  UserInfo,
} from "@/lib/api";

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} Б`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} КБ`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} МБ`;
}

function fmtDate(iso: string): string {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "—";
  return new Intl.DateTimeFormat("ru-RU", {
    day: "2-digit",
    month: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  }).format(d);
}

function fileIcon(kind: string) {
  if (kind === "image") return <ImageIcon size={16} className="text-accent shrink-0" />;
  if (kind === "pdf") return <FileText size={16} className="text-red-400 shrink-0" />;
  return <FileIcon size={16} className="text-secondary shrink-0" />;
}

/** User file library panel (style: same as Настройки — right slide-in).
 *  Lists all files the user uploaded to the chat; supports preview
 *  (images), download and delete. Quota: 50MB per account. */
export function LibraryPanel({ user, onClose }: { user: UserInfo; onClose: () => void }) {
  const [files, setFiles] = useState<UserFileItem[]>([]);
  const [quotaUsed, setQuotaUsed] = useState(0);
  const [quotaLimit, setQuotaLimit] = useState(50 * 1024 * 1024);
  const [error, setError] = useState<string | null>(null);
  const [preview, setPreview] = useState<UserFileItem | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<UserFileItem | null>(null);

  const load = useCallback(() => {
    fetchFiles()
      .then((res) => {
        setFiles(res.files);
        setQuotaUsed(res.quota_used);
        setQuotaLimit(res.quota_limit);
        setError(null);
      })
      .catch((e) => setError(e instanceof Error ? e.message : "Не удалось загрузить библиотеку"));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const quotaPct = quotaLimit > 0 ? Math.min(100, (quotaUsed / quotaLimit) * 100) : 0;

  const doDelete = async () => {
    if (!confirmDelete) return;
    try {
      await deleteFile(confirmDelete.id);
      if (preview?.id === confirmDelete.id) setPreview(null);
      load();
    } catch {
      setError("Не удалось удалить файл");
    }
    setConfirmDelete(null);
  };

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center md:p-4">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-[2px]" onClick={onClose} aria-hidden />
      <div className="relative flex flex-col w-full h-full md:max-w-xl md:max-h-[85vh] md:h-auto md:rounded-2xl border border-strongline bg-app shadow-2xl overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b border-hairline">
          <h2 className="text-lg font-medium text-primary flex items-center gap-2">
            <LibraryBig size={18} className="text-accent" /> Библиотека файлов
          </h2>
          <Tip label="Закрыть" side="bottom">
            <button
              onClick={onClose}
              className="p-1.5 rounded-lg text-tertiary hover:text-primary hover:bg-hoverbg transition-colors"
            >
              <X size={18} />
            </button>
          </Tip>
        </div>

        {/* Quota */}
        <div className="px-5 py-4 border-b border-hairline space-y-2">
          <div className="flex items-center justify-between text-xs text-tertiary">
            <span>Использовано</span>
            <span>
              {formatSize(quotaUsed)} / {formatSize(quotaLimit)}
            </span>
          </div>
          <div className="h-1.5 rounded-full bg-hoverbg overflow-hidden">
            <div
              className="h-full rounded-full bg-accent transition-all duration-300"
              style={{ width: `${quotaPct}%` }}
            />
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-2">
          {error && (
            <div className="text-xs px-3 py-2 rounded-lg bg-red-500/10 text-red-400">{error}</div>
          )}
          {!error && files.length === 0 && (
            <div className="text-sm text-tertiary text-center py-12">
              Библиотека пуста.
              <br />
              Прикрепляйте файлы через «+» в чате — они появятся здесь.
            </div>
          )}
          {files.map((f) => (
            <div
              key={f.id}
              className="group flex items-center gap-3 rounded-xl border border-hairline bg-hoverbg/40 p-3 hover:bg-hoverbg transition-colors"
            >
              <button
                onClick={() => f.kind === "image" && setPreview(f)}
                className={cn(
                  "w-10 h-10 rounded-lg bg-elevated border border-strongline flex items-center justify-center shrink-0",
                  f.kind === "image" ? "cursor-pointer" : "cursor-default"
                )}
              >
                {f.kind === "image" && preview?.id !== f.id ? (
                  <ImageIcon size={16} className="text-accent" />
                ) : (
                  fileIcon(f.kind)
                )}
              </button>
              <div className="flex-1 min-w-0">
                <div className="text-sm text-primary truncate">{f.name}</div>
                <div className="text-[11px] text-tertiary">
                  {formatSize(f.size)} · {fmtDate(f.created_at)}
                </div>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <Tip label="Скачать" side="top">
                  <a
                    href={fileDownloadUrl(f.id)}
                    download={f.name}
                    className="p-1.5 rounded-lg text-secondary hover:text-primary hover:bg-hoverbg2 transition-colors"
                  >
                    <Download size={14} />
                  </a>
                </Tip>
                <Tip label="Удалить" side="top">
                  <button
                    onClick={() => setConfirmDelete(f)}
                    className="p-1.5 rounded-lg text-secondary hover:text-red-400 hover:bg-hoverbg2 transition-colors"
                  >
                    <Trash2 size={14} />
                  </button>
                </Tip>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Image preview (in-palette modal) */}
      {preview && (
        <div
          className="fixed inset-0 z-[110] flex items-center justify-center bg-black/70"
          onClick={() => setPreview(null)}
        >
          <div
            className="relative max-w-lg w-full mx-4 rounded-xl border border-strongline bg-elevated p-3"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-2 px-1">
              <span className="text-sm text-primary truncate">{preview.name}</span>
              <button
                onClick={() => setPreview(null)}
                className="p-1 rounded text-tertiary hover:text-primary transition-colors"
                aria-label="Закрыть превью"
              >
                <X size={16} />
              </button>
            </div>
            <a href={fileDownloadUrl(preview.id)} download={preview.name} className="block">
              <img
                src={fileDownloadUrl(preview.id)}
                alt={preview.name}
                className="w-full max-h-[70vh] object-contain rounded-lg"
              />
            </a>
          </div>
        </div>
      )}

      {/* Delete confirmation (in-palette) */}
      {confirmDelete && (
        <div className="fixed inset-0 z-[120] flex items-center justify-center">
          <div className="absolute inset-0 bg-black/60" onClick={() => setConfirmDelete(null)} aria-hidden />
          <div className="relative w-full max-w-sm mx-4 rounded-xl border border-strongline bg-elevated p-5 shadow-2xl">
            <h3 className="text-base font-medium text-primary">Удалить файл?</h3>
            <p className="mt-2 text-sm text-secondary">
              «{confirmDelete.name}» будет удалён из библиотеки без возможности восстановления.
            </p>
            <div className="mt-5 flex justify-end gap-2">
              <button
                onClick={() => setConfirmDelete(null)}
                className="h-9 px-4 rounded-lg text-sm text-secondary hover:bg-hoverbg2 hover:text-primary transition-colors"
              >
                Отмена
              </button>
              <button
                onClick={doDelete}
                className="h-9 px-4 rounded-lg text-sm font-medium bg-red-500/90 text-white hover:bg-red-500 transition-colors"
              >
                Удалить
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
